import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Header.css";

function Header({ toggleSidebar, isSidebarOpen, isMobile }) {
  const [avatarImage, setAvatarImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  
  // Mock notifications data - replace with real data later
  const [notifications] = useState([
    {
      id: 1,
      title: "New User Registration",
      message: "A new user has registered",
      time: "5 minutes ago",
      read: false,
      type: "user"
    },
    {
      id: 2,
      title: "Payment Received",
      message: "Payment of ৳5000 received",
      time: "1 hour ago",
      read: false,
      type: "payment"
    },
    {
      id: 3,
      title: "Withdrawal Request",
      message: "New withdrawal request pending",
      time: "2 hours ago",
      read: false,
      type: "withdraw"
    },
    {
      id: 4,
      title: "System Update",
      message: "System maintenance scheduled",
      time: "1 day ago",
      read: true,
      type: "system"
    },
    {
      id: 5,
      title: "Support Ticket",
      message: "New support ticket created",
      time: "2 days ago",
      read: true,
      type: "support"
    }
  ]);

  useEffect(() => {
    // Get admin photo from localStorage
    const adminPhoto = localStorage.getItem("photo") || localStorage.getItem("adminPhoto");
    
    // Set avatar image if available
    if (adminPhoto) {
      setAvatarImage(adminPhoto);
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
    };

    if (dropdownOpen || notificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, notificationDropdownOpen]);

  // Toggle dropdown
  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(!dropdownOpen);
  };

  // Log out function
  const handleLogout = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(false);
    localStorage.removeItem("token");
    localStorage.removeItem("id");
    localStorage.removeItem("IsAdmin");
    localStorage.removeItem("AdminType");
    localStorage.removeItem("adminName");
    localStorage.removeItem("adminPhoto");
    localStorage.removeItem("name");
    localStorage.removeItem("photo");
    window.location.href = "/login";
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleMenuItemClick = () => {
    setDropdownOpen(false);
  };

  // Toggle notification dropdown
  const toggleNotificationDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setNotificationDropdownOpen(!notificationDropdownOpen);
    // Close avatar dropdown if open
    if (dropdownOpen) {
      setDropdownOpen(false);
    }
  };

  const handleNotificationClick = (notificationId) => {
    // Handle notification click - mark as read, navigate, etc.
    console.log("Notification clicked:", notificationId);
    // setNotificationDropdownOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case "user":
        return "person_add";
      case "payment":
        return "payment";
      case "withdraw":
        return "account_balance_wallet";
      case "system":
        return "settings";
      case "support":
        return "support_agent";
      default:
        return "notifications";
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <header className="app-header">
      {/* Left section - Menu icon on mobile, Logo on desktop */}
      <div className="header-left">
        {isMobile ? (
          <button className="hamburger" onClick={toggleSidebar} aria-label="Toggle menu">
            <span className="material-icons">
              {isSidebarOpen ? "close" : "menu"}
            </span>
          </button>
        ) : (
          <div className="logo-container">
            <Link to="/dashboard" className="logo-link">
              <img
                src="/logo.png"
                alt="বিবাহ বাংলা"
                className="header-logo-image"
              />
            </Link>
          </div>
        )}
      </div>

      {/* Center section - Logo on mobile, empty on desktop */}
      <div className="header-center">
        {isMobile && (
          <div className="logo-container">
            <Link to="/dashboard" className="logo-link">
              <img
                src="/logo.png"
                alt="বিবাহ বাংলা"
                className="header-logo-image"
              />
            </Link>
          </div>
        )}
      </div>

      {/* Right section - Notification and Avatar */}
      <div className="header-right">
        <div className="notification-dropdown-container" ref={notificationDropdownRef}>
          <div 
            className="notification-icon" 
            onClick={toggleNotificationDropdown}
            style={{ cursor: 'pointer' }}
            title="Notifications"
          >
            <span className="material-icons">notifications</span>
            {unreadCount > 0 && (
              <span className="badge">{unreadCount}</span>
            )}
          </div>
          
          {notificationDropdownOpen && (
            <div className="notification-dropdown">
              <div className="notification-dropdown-header">
                <h3 className="notification-dropdown-title">Notifications</h3>
                <button 
                  className="notification-mark-all-read"
                  onClick={() => console.log("Mark all as read")}
                >
                  Mark all as read
                </button>
              </div>
              
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">
                    <span className="material-icons">notifications_none</span>
                    <p>No notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`notification-item ${!notification.read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification.id)}
                    >
                      <div className="notification-icon-wrapper">
                        <span className="material-icons notification-type-icon">
                          {getNotificationIcon(notification.type)}
                        </span>
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-message">{notification.message}</div>
                        <div className="notification-time">{notification.time}</div>
                      </div>
                      {!notification.read && (
                        <div className="notification-unread-dot"></div>
                      )}
                    </div>
                  ))
                )}
              </div>
              
              <div className="notification-dropdown-footer">
                <button 
                  className="notification-view-all"
                  onClick={() => {
                    setNotificationDropdownOpen(false);
                    // Navigate to notifications page
                    window.location.href = "/notifications";
                  }}
                >
                  View All Notifications
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="avatar-dropdown-container" ref={dropdownRef}>
          <div 
            className="avatar" 
            onClick={toggleDropdown} 
            style={{ cursor: 'pointer' }}
            title="Click to open menu"
          >
            {avatarImage && !imageError ? (
              <img 
                src={avatarImage} 
                alt="Admin" 
                className="profile-image"
                onError={handleImageError}
              />
            ) : (
              <span className="material-icons avatar-icon">person</span>
            )}
          </div>
          
          {dropdownOpen && (
            <div className="avatar-dropdown">
              <div className="dropdown-header">
                <div className="dropdown-avatar">
                  {avatarImage && !imageError ? (
                    <img 
                      src={avatarImage} 
                      alt="Admin" 
                      className="dropdown-profile-image"
                      onError={handleImageError}
                    />
                  ) : (
                    <span className="material-icons dropdown-icon">person</span>
                  )}
                </div>
                <div className="dropdown-user-info">
                  <div className="dropdown-username">
                    {localStorage.getItem("name") || "Admin"}
                  </div>
                  <div className="dropdown-user-role">
                    {localStorage.getItem("AdminType") || "Administrator"}
                  </div>
                </div>
              </div>
              
              <div className="dropdown-divider"></div>
              
              <Link 
                to="/profile" 
                className="dropdown-item"
                onClick={handleMenuItemClick}
              >
                <span className="material-icons">account_circle</span>
                <span>Profile</span>
              </Link>
              
              <Link 
                to="/settings" 
                className="dropdown-item"
                onClick={handleMenuItemClick}
              >
                <span className="material-icons">settings</span>
                <span>Settings</span>
              </Link>
              
              <div className="dropdown-divider"></div>
              
              <div 
                className="dropdown-item dropdown-item-logout"
                onClick={handleLogout}
              >
                <span className="material-icons">logout</span>
                <span>Logout</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
